import * as React from 'react';
import styled from 'styled-components';
import { config } from 'react-spring';
import Banner, { BannerProps } from '@mybet/core/components/Banner';
import ButtonBase from '@mybet/components/Button';
import Flex from '@mybet/components/Flex';
import TypographyBase from '@mybet/components/Typography';
import SlideTo from '@mybet/core/components/Animations/SlideTo';
import FadeIn from '@mybet/core/components/Animations/FadeIn';
import useMatchMedia from '@mybet/core/utils/useMatchMedia';

const ContentRoot = styled.div((props) => ({
  padding: props.theme.spacing(3, 1, 11),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',

  [props.theme.breakpoints.up('md')]: {
    padding: props.theme.spacing(4, 1, 7),
  },
}));

const Footer = styled(Flex)((props) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  width: '100%',
  padding: props.theme.spacing(2),
}));

const Typography = styled(TypographyBase)({
  cursor: 'default',
});

const HeadlineRoot = styled(Flex)((props) => ({
  width: '100%',
  margin: props.theme.spacing(0, 'auto', 3),
  padding: props.theme.spacing(0, 5),
  height: 24,

  [props.theme.breakpoints.up('sm')]: {
    margin: props.theme.spacing(0, 'auto', 5),
    padding: props.theme.spacing(0, 2),
    height: 32,
  },
}));

const Button = styled(ButtonBase)((props) => ({
  minWidth: '124px',
  marginLeft: props.theme.spacing(0.5),
}));

const Root = styled.div({
  cursor: 'pointer',
  width: '100%',
  maxWidth: '816px',
  margin: '0 auto',
  boxShadow:
    '0px 8px 10px rgba(0, 0, 0, 0.2), 0px 16px 24px rgba(0, 0, 0, 0.14), 0px 6px 30px rgba(0, 0, 0, 0.12);',

  '*': {
    cursor: 'pointer',
  },
});

type Props = Pick<BannerProps, 'ImageProps'> & { onClick(): void };

const RaffleCampaignBanner: React.FC<Props> = (props) => {
  const { onClick, ImageProps } = props;
  const { matches: mdMatches } = useMatchMedia('md');
  const { matches: smMatches } = useMatchMedia('sm');
  const headlineChangeTimeout = React.useRef(0);
  const headlineChangeStartTimeout = React.useRef(0);
  const [headlineIndex, setHeadlineIndex] = React.useState(0);

  React.useEffect(() => {
    function changeHeadline() {
      headlineChangeTimeout.current &&
        clearTimeout(headlineChangeTimeout.current);
      setHeadlineIndex((current) => (current === 0 ? 1 : 0));
      headlineChangeTimeout.current = window.setTimeout(changeHeadline, 4000);
    }

    headlineChangeStartTimeout.current = window.setTimeout(
      changeHeadline,
      4000
    );
    return () => {
      headlineChangeStartTimeout.current &&
        clearTimeout(headlineChangeStartTimeout.current);
      headlineChangeTimeout.current &&
        clearTimeout(headlineChangeTimeout.current);
    };
  }, []);

  return (
    <Root onClick={mdMatches ? onClick : undefined}>
      <Banner
        imageSrc={
          smMatches
            ? 'https://images.ctfassets.net/ghpfhv05r7vl/zOxP1btSB3HW58kzsdXmM/121853893328da08fcf9457826ac0555/raffle-hero-desktop.jpg'
            : 'https://images.ctfassets.net/ghpfhv05r7vl/q5wnKMkD3BPlzRie0BQdH/a398591a5fb580a128a0aeefbc045b58/raffle-hero-mobile.jpg'
        }
        ImageProps={ImageProps}
      >
        <ContentRoot>
          <HeadlineRoot flexDirection="column" alignItems="center">
            <SlideTo from="left" delay={2000} style={{ textAlign: 'center' }}>
              <Typography
                variant={mdMatches ? 'headline2' : 'headline3'}
                color="link"
                align="center"
                gutterBottom={0.5}
              >
                Jetzt gewinnen
              </Typography>
            </SlideTo>
            <SlideTo from="right" delay={2000} style={{ textAlign: 'center' }}>
              <>
                <FadeIn
                  show={headlineIndex === 0}
                  style={{ position: 'absolute', width: '100%' }}
                  config={config.molasses}
                  delay={500}
                  immediate={headlineIndex !== 0}
                >
                  <Typography
                    variant={mdMatches ? 'headline2' : 'headline3'}
                    color="textSecondary"
                    align="center"
                  >
                    einen Tesla Model 3
                  </Typography>
                </FadeIn>

                <FadeIn
                  show={headlineIndex === 1}
                  style={{ position: 'absolute', width: '100%' }}
                  config={config.molasses}
                  immediate={headlineIndex !== 1}
                  delay={500}
                >
                  <Typography
                    variant={mdMatches ? 'headline2' : 'headline3'}
                    color="textSecondary"
                    align="center"
                  >
                    3 x 2 Reisen zur WM in Katar
                  </Typography>
                </FadeIn>
              </>
            </SlideTo>
          </HeadlineRoot>
        </ContentRoot>
        <Footer justifyContent="space-between" alignItems="center">
          <Typography
            variant={mdMatches ? 'body4' : 'body5'}
            fontWeight={500}
            color="textSecondary"
          >
            Registrieren, einzahlen und an der Verlosung teilnehmen.
          </Typography>
          <Button
            variant="secondary"
            size="medium"
            onClick={!mdMatches ? onClick : undefined}
          >
            Mehr erfahren
          </Button>
        </Footer>
      </Banner>
    </Root>
  );
};

export default RaffleCampaignBanner;
